import React, { useEffect, useMemo, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import {
    SWAN_BASE_URL_MAP,
    ScreenClassProvider,
    SwanHead,
    SwanProvider,
    getRootClassNames,
    getSwanJsUrl,
} from '@vp/swan';
import enUS from '@vp/swan/i18n/en-us.json';

const translationModules: Record<string, () => Promise<{ default: Record<string, string> }>> = {
    'da-dk': () => import('@vp/swan/i18n/da-dk.json'),
    'de-at': () => import('@vp/swan/i18n/de-at.json'),
    'de-ch': () => import('@vp/swan/i18n/de-ch.json'),
    'de-de': () => import('@vp/swan/i18n/de-de.json'),
    'en-au': () => import('@vp/swan/i18n/en-au.json'),
    'en-ca': () => import('@vp/swan/i18n/en-ca.json'),
    'en-gb': () => import('@vp/swan/i18n/en-gb.json'),
    'en-ie': () => import('@vp/swan/i18n/en-ie.json'),
    'en-in': () => import('@vp/swan/i18n/en-in.json'),
    'en-nz': () => import('@vp/swan/i18n/en-nz.json'),
    'en-sg': () => import('@vp/swan/i18n/en-sg.json'),
    'en-us': () => import('@vp/swan/i18n/en-us.json'),
    'es-es': () => import('@vp/swan/i18n/es-es.json'),
    'es-us': () => import('@vp/swan/i18n/es-us.json'),
    'fi-fi': () => import('@vp/swan/i18n/fi-fi.json'),
    'fr-be': () => import('@vp/swan/i18n/fr-be.json'),
    'fr-ca': () => import('@vp/swan/i18n/fr-ca.json'),
    'fr-ch': () => import('@vp/swan/i18n/fr-ch.json'),
    'fr-fr': () => import('@vp/swan/i18n/fr-fr.json'),
    'it-ch': () => import('@vp/swan/i18n/it-ch.json'),
    'it-it': () => import('@vp/swan/i18n/it-it.json'),
    'nb-no': () => import('@vp/swan/i18n/nb-no.json'),
    'nl-be': () => import('@vp/swan/i18n/nl-be.json'),
    'nl-nl': () => import('@vp/swan/i18n/nl-nl.json'),
    'pt-pt': () => import('@vp/swan/i18n/pt-pt.json'),
    'sv-se': () => import('@vp/swan/i18n/sv-se.json'),
};

function useSwanTranslations(locale?: string): Record<string, string> {
    const [translations, setTranslations] = useState<Record<string, string>>(enUS);
    const normalizedLocale = useMemo(() => (locale ?? 'en-us').toLowerCase(), [locale]);

    useEffect(() => {
        const loader = translationModules[normalizedLocale];
        if (loader) {
            loader().then((mod) => setTranslations(mod.default ?? mod));
        }
    }, [normalizedLocale]);

    return translations;
}

interface SwanConfigurationProps {
    children: React.ReactNode;
    locale?: string;
}

export const SwanConfiguration = ({ children, locale }: SwanConfigurationProps) => {
    const rootClassName = getRootClassNames();
    const translations = useSwanTranslations(locale);

    useEffect(() => {
        if (typeof document === 'undefined') return;

        document.body.className = rootClassName;
        const src = getSwanJsUrl('popover', undefined, SWAN_BASE_URL_MAP.default.cdn);
        if (src) {
            const s = document.createElement('script');
            s.setAttribute('src', src ?? '');
            s.setAttribute('data-swan', 'popover');
            s.async = true;
            document.body.appendChild(s);
        }
    }, [rootClassName]);

    return (
        <HelmetProvider>
            <ScreenClassProvider>
                <SwanProvider
                    swanBaseUrl={SWAN_BASE_URL_MAP.default.cdn}
                    swanLocale={locale}
                    translations={translations}
                >
                    <SwanHead />
                    {children}
                </SwanProvider>
            </ScreenClassProvider>
        </HelmetProvider>
    );
};
